import * as d3 from 'd3';
import { TDrawModel } from '../../draw.model';
import {
  TCircleElements,
  TEntityActionCb,
  TEntityActions,
  TRectElements,
  TStripElement,
  TTimelineModel,
} from '../../timeline.types';

const HIGHLIGHTED_STRIP = {
  BG_COLOR: '#FFEB3B',
  TEXT_COLOR: '#000',
  BORDER_COLOR: '#000',
};

export const DRAW_ELEMENTS = {
  drawElements: (
    elements: d3.Selection<
      d3.BaseType | SVGSVGElement,
      TTimelineModel['entities'][number]['entities'][number]['elements'][number],
      d3.BaseType | SVGGElement,
      TTimelineModel['entities'][number]['entities'][number]
    >,
    drawModel: TDrawModel,
    stripEventCb: TEntityActionCb,
  ) => {
    // Clean up
    elements.selectAll('rect.elm-mask-background').remove();
    elements
      .filter((d) => d.type === 'STRIP' && d.mask)
      .selectAll('.elm-mask-background')
      .data((d) => [d] as Required<TStripElement>[])
      .join('rect')
      .classed('elm-mask-background', true)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('font-weight', 'bold')
      .attr('fill', (d) => (d.mask ? 'black' : 'transparent'))
      .attr('rx', '4')
      .attr('ry', '4');

    // clean up
    elements.selectAll('rect.elm').remove();
    elements
      .filter((d) => d.type === 'STRIP' || d.type === 'MARKER' || d.type === 'MESH')
      .selectAll('.elm')
      .data((d) => [d] as TRectElements[])
      .join('rect')
      .classed('elm', true)
      .attr('fill', (d) => (d.isStripHighlighted && d.type === 'STRIP' ? HIGHLIGHTED_STRIP.BG_COLOR : d.color))
      .attr('stroke', (d) =>
        d.isStripHighlighted && d.type === 'STRIP' ? HIGHLIGHTED_STRIP.BORDER_COLOR : d.borderColor || '',
      )
      .attr('stroke-width', (d) => (d.borderColor || d.isStripHighlighted ? 1 : 0))
      .attr('width', '100%')
      .attr('height', (d) => {
        return (drawModel.layout.stackHeights[d.stackCount] * (d.heightInPercent ?? 100)) / 100;
      })
      .attr('mask', (d) => {
        switch (d.type) {
          case 'STRIP':
          case 'MARKER': {
            return d.mask ? `url(#${drawModel.defsIds['mask-zig-zag']})` : '';
          }
          case 'MESH': {
            return d.mask ? `url(#${drawModel.defsIds['mask-mesh']})` : '';
          }
        }
      })
      .attr('rx', '4')
      .attr('ry', '4');

    elements.selectAll('rect.elm-strip-pattern').remove();
    elements
      .filter((d) => d.type === 'STRIP' && !!d.progressFill)
      .selectAll('.elm-strip-pattern')
      .data((d) => [d] as TStripElement[])
      .join('rect')
      .classed('elm-strip-pattern', true)
      .attr('fill', (d) => `url(#${d.progressFill?.pattern || ''})`)
      .attr('width', (d) => `${d.progressFill?.fillInPercent || 0}%`)
      .attr('height', (d) => {
        return (drawModel.layout.stackHeights[d.stackCount] * (d.heightInPercent ?? 100)) / 100;
      })
      .attr('rx', '4')
      .attr('ry', '4');

    elements.selectAll('.elm-text-group').remove();
    const textGroup = elements
      .filter((d) => d.type === 'STRIP' && !!d.textList)
      .selectAll('.elm-text-group')
      .data((d) => [d] as TStripElement[])
      .join('g')
      .classed('elm-text-group', true);

    // Clean up before adding text
    textGroup.selectAll('text.elm-text').remove();
    textGroup
      .selectAll('.elm-text')
      .data((d) =>
        (d.textList || []).map((textEl) => {
          return { textElementInfo: textEl, stripElement: d as TStripElement };
        }),
      )
      .join('text')
      .classed('elm-text', true)
      .style('pointer-events', 'none')
      .text((d) => d.textElementInfo.value)
      .attr('font-size', (d) => d.textElementInfo.fontSizeInPx ?? 14)
      .attr('font-weight', 'bold')
      .attr('fill', (d) => (d.stripElement.isStripHighlighted ? HIGHLIGHTED_STRIP.TEXT_COLOR : d.textElementInfo.color))
      .attr('text-anchor', (d) =>
        d.textElementInfo.justify === 'center' ? 'middle' : d.textElementInfo.justify === 'right' ? 'end' : 'start',
      )
      .attr('x', (d) =>
        d.textElementInfo.justify === 'center' ? '50%' : d.textElementInfo.justify === 'right' ? '100%' : 4,
      )
      .attr('dominant-baseline', (d) => {
        const align = d.textElementInfo.align ?? 'center';
        if (align === 'center') return 'central';
        if (align === 'bottom') return 'baseline';
        return 'text-before-edge';
      })
      .attr('y', (d) => {
        const align = d.textElementInfo.align ?? 'center';
        const padding = 4;
        const stackHeightInPx = drawModel.layout.stackHeights[d.stripElement.stackCount];
        const height = ((d.stripElement.heightInPercent ?? 100) * stackHeightInPx) / 100;

        if (align === 'top') return padding;
        if (align === 'bottom') return height - padding;
        return height / 2;
      });

    // Clean up
    elements.selectAll('rect.elm-text-border').remove();
    elements
      .filter((d) => d.type === 'STRIP' && (!!d.textBorderColor || !!d.isStripHighlighted))
      .selectAll('.elm-text-border')
      .data((d) => [d] as Required<TStripElement>[])
      .join('rect')
      .classed('elm-text-border', true)
      .attr('x', 1)
      .attr('y', 1)
      .attr('width', 'calc(100% - 2px)')
      .attr('height', 'calc(100% - 2px)')
      .attr('font-weight', 'bold')
      .attr('stroke', (d) => (d.isStripHighlighted ? HIGHLIGHTED_STRIP.BORDER_COLOR : d.textBorderColor))
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', (d) => (d.isStripHighlighted ? '5, 5' : '0'))
      .attr('fill', 'transparent')
      .attr('rx', '4')
      .attr('ry', '4');

    // Clean up
    elements.selectAll('circle.elm-circle').remove();
    elements
      .filter((d) => d.type === 'CIRCLE')
      .selectAll('.elm-circle')
      .data((d) => [d] as TCircleElements[])
      .join('circle')
      .classed('elm-circle', true)
      .attr('width', '100%')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', drawModel.timelineModel.boardView.boardViewType === 'MONTHLY' ? 2 : 5)
      .attr(
        'transform',
        (d) => `translate(${drawModel.layout.widthOfDay / 2}, ${drawModel.layout.stackHeights[d.stackCount] / 2})`,
      )
      .attr('fill', (d) => d.color)
      .attr('stroke', 'black')
      .attr('stroke-width', '2');

    // Clean up before adding icons
    elements.selectAll('.icon-group').remove();
    const iconGroup = elements
      .filter((d) => d.type === 'STRIP' && d.iconList && d.iconList.length > 0)
      .selectAll('.icon-group')
      .data((d) => [d] as TStripElement[])
      .join('g')
      .classed('icon-group', true);

    iconGroup.selectAll('.icon-svg').remove();
    iconGroup
      .selectAll('.icon-svg')
      .data((d) =>
        d.iconList.map((icon, index) => {
          return { iconElementInfo: icon, stripElement: d as TStripElement, index: index };
        }),
      )
      .join('svg')
      .each(function (d) {
        if (d.iconElementInfo.background) {
          d3.select(this)
            .append('circle')
            .attr('cx', 11)
            .attr('cy', 11)
            .attr('r', 14)
            .attr('fill', d.iconElementInfo.background);
        }

        const path = d3
          .select(this)
          .append('path')
          .attr('d', d.iconElementInfo.svgPath)
          .attr('fill', d.iconElementInfo.svgColor || '#0F31A9');
        if (d.iconElementInfo.fillRule) {
          path.attr('fill-rule', d.iconElementInfo.fillRule);
        }
      })
      .classed('icon-svg', true)
      .attr('width', 18)
      .attr('height', 18)
      .attr('x', function (d) {
        return `calc(100% - ${22 * (d.index + 1)}px)`;
      })
      .attr('y', '3.5px')
      .attr('viewBox', '0 0 22 22')
      .attr('fill', 'none')
      .on('click.icon_event', (event$: Event, el) => {
        if (el.iconElementInfo.clickAction) {
          console.log('icon click called');
          console.log(el.stripElement.data);
          event$.stopPropagation();
          const iconSvgElement = document.querySelector('.icon-svg');
          const closestSvgElement = iconSvgElement?.closest('svg');

          if (closestSvgElement) {
            const parentSvgElementData = d3.select(closestSvgElement).datum();

            // Get the parent's parent SVG element
            const ancestorSvgElement = closestSvgElement.closest('svg');

            if (ancestorSvgElement) {
              const entityGroupData = d3.select(ancestorSvgElement).datum();

              const emitVal = {
                type: 'STRIP',
                action: el.iconElementInfo.clickAction,
                event,
                elementData: el.stripElement.data,
                entityData: parentSvgElementData,
                entityGroupData: entityGroupData,
              };
              stripEventCb(emitVal as TEntityActions);
            }
          }
        }
      });
  },
};
